import { ErrorSchema } from '@mint-sdk/api-core';
import { z } from 'zod';

const TextFilterCondition = z.enum([
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
  'blank',
  'not_blank',
]);

const NumberFilterCondition = z.enum([
  'not_equals',
  'gt',
  'gte',
  'lt',
  'lte',
  'blank',
  'not_blank',
]);

const RangeFilterCondition = z.enum(['between']);

export const ComparisonOperator = z.enum(['AND', 'OR']);

export const TextFilter = z.object({
  condition: TextFilterCondition,
  value: z.string().nullable().optional(),
});

export const NumberFilter = z.object({
  condition: NumberFilterCondition,
  value: z.string().nullable().optional(),
});

export const ComparisonNumberFilter = z
  .object({
    operator: ComparisonOperator,
    filters: z.array(NumberFilter).max(2),
  })
  .or(NumberFilter);

export const RangeFilter = z.object({
  valueFrom: z.string(),
  valueTo: z.string(),
  condition: RangeFilterCondition,
});

export const SetFilter = z.object({
  values: z.array(z.string()),
});

const MultiTextFilter = z.object({
  operator: ComparisonOperator,
  filters: z.array(TextFilter),
});

export const NameFilter = z.object({
  filters: z.array(SetFilter.or(MultiTextFilter).or(TextFilter)).optional(),
  operator: ComparisonOperator.optional(),
});

const SetFilterCollection = z.object({
  filters: z.array(SetFilter),
});

export const RequestFilterValue = z.union([
  NumberFilter,
  RangeFilter,
  TextFilter,
  NameFilter,
  MultiTextFilter,
  SetFilter,
  SetFilterCollection,
  ComparisonNumberFilter,
]);

export const RequestFilterParams = z.record(z.string(), RequestFilterValue);

export const GetFilterOptionsRequestSchema = z.object({
  virtualDspCampaignId: z.string(),
  filters: RequestFilterParams.nullable().optional(),
});

export const MdmFilterCondition = z.union([
  z.object({
    condition: TextFilterCondition.or(NumberFilterCondition),
    value: z.string().nullable().optional(),
  }),
  z.object({
    condition: RangeFilterCondition,
    valueFrom: z.string(),
    valueTo: z.string(),
  }),
]);

export const MdmFilterChoices = z.object({
  value: z.array(z.string()),
});

export const MdmRequestFilterValue = z.object({
  operator: ComparisonOperator.optional(),
  filters: z.array(MdmFilterCondition).optional(),
  filterChoices: MdmFilterChoices.optional(),
});

export const MdmRequestFilterParams = z.record(
  z.string(),
  MdmRequestFilterValue,
);

export const GetFilterOptionsRequestSchemaMdm = z.object({
  virtualDspCampaignId: z.string(),
  filters: MdmRequestFilterParams.nullable().optional(),
});

const KnownResPropertiesSchema = z.object({
  name: z.array(z.string()),
  priceType: z.array(z.string()),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
});

const UnknownResPropertiesSchema = z.record(
  z.string(),
  z.union([z.array(z.string()), z.string(), z.null()]),
);

const AdditionalSchema = z.intersection(
  UnknownResPropertiesSchema,
  KnownResPropertiesSchema,
);

const MdmFilterOptionAttributeValueSchema = z.union([
  z.array(z.string()),
  z.string(),
  z.null(),
]);

const MdmFilterOptionsAttributesSchema = z.record(
  z.string(),
  MdmFilterOptionAttributeValueSchema,
);

export const MdmFilterOptionsAdditionalSchema = z
  .object({
    attributes: MdmFilterOptionsAttributesSchema,
  })
  .loose();

export const GetFilterOptionsResponseSchema = z.object({
  data: z.object({
    additional: AdditionalSchema,
  }),
  error: ErrorSchema.nullable(),
});

export const GetFilterOptionsMdmResponseSchema = z.object({
  data: z
    .object({
      additional: MdmFilterOptionsAdditionalSchema,
    })
    .loose(),
  error: ErrorSchema.nullable(),
});

export const GetFilterOptionsQuerySchema = z.object({
  hash: z.string().optional(),
});

export type FilterParams = z.infer<typeof RequestFilterParams>;
export type MdmFilterParams = z.infer<typeof MdmRequestFilterParams>;
export type MdmFilterValue = z.infer<typeof MdmRequestFilterValue>;
export type TextFilterConditionType = z.infer<typeof TextFilterCondition>;
export type NumberFilterConditionType = z.infer<typeof NumberFilterCondition>;
export type GetFilterOptionsResponse = z.infer<
  typeof GetFilterOptionsResponseSchema
>;
export type GetFilterOptionsMdmResponse = z.infer<
  typeof GetFilterOptionsMdmResponseSchema
>;
export type MdmFilterOptionsAdditional = z.infer<
  typeof MdmFilterOptionsAdditionalSchema
>;
